import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useIdleTimerContext } from 'react-idle-timer';
import TinyColor from 'tinycolor2';

import { Button } from 'shared/components';
import { DialogContainer, DialogHeading } from 'src/components/modals/components/simple-dialog';

export type CountdownModalProps = {
  startNewSession: () => void;
  closeAndContinue: () => void;
  timeLeftInSeconds: number;
  title: React.ReactNode | string;
  subtitle?: string;
  primaryButtonText: string;
  secondaryButtonText: string;
};

export const CountdownModal = ({
  startNewSession,
  closeAndContinue,
  timeLeftInSeconds = 10,
  title,
  subtitle,
  primaryButtonText,
  secondaryButtonText,
}: CountdownModalProps): JSX.Element => {
  const idleTimer = useIdleTimerContext();
  const [timeLeft, setTimeLeft] = useState(timeLeftInSeconds);
  const [fillPercentage] = useState(timeLeft);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    idleTimer?.pause();
  }, [idleTimer]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (timeLeft > 0) {
        setTimeLeft(timeLeft - 1);
      } else {
        startNewSession();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [timeLeft, startNewSession]);

  return (
    <Container>
      <Heading>{title}</Heading>
      {subtitle && <SubText>{subtitle}</SubText>}

      <BaseButton inverted onClick={closeAndContinue} mb={12} fluid data-testid='idle-warning-continue-shopping'>
        {secondaryButtonText}
      </BaseButton>
      <ButtonAnimation fillPercentage={fillPercentage} onClick={startNewSession} data-testid='idle-warning-new-session'>
        {primaryButtonText} <Countdown>({timeLeft})</Countdown>
      </ButtonAnimation>
    </Container>
  );
};

const Container = styled(DialogContainer)`
  max-width: 800px;
  width: 100%;
`;

const Heading = styled(DialogHeading)`
  font-size: 36px;
  margin-bottom: 0;
`;

const Countdown = styled.span`
  font-feature-settings: 'liga' 0, 'clig' 0, 'calt' 0, 'ss01' 0, 'ss02' 0;
`;

const BaseButton = styled(Button)`
  font-size: 24px;
  font-weight: 600;
  width: 100%;
  max-width: 600px;
  height: 64px;
  text-transform: none;
`;

const ButtonAnimation = styled(BaseButton)<{ fillPercentage: number }>`
  ${({ theme }) => {
    const buttonColor = (theme?.customized?.colors?.buttonsLinks ?? theme.colors.primaryBlue) as string;
    return `
      background-image: linear-gradient(to right, ${buttonColor} 0%, ${buttonColor} 100%);
      background-repeat: no-repeat;
      background-color: ${TinyColor.mix(buttonColor, '#fff', 20).toString()};
    `;
  }}
  background-size: 0% 100%;
  animation: fill ${({ fillPercentage }) => fillPercentage}s linear forwards;

  @keyframes fill {
    from {
      background-size: 0% 100%;
    }
    to {
      background-size: 100% 100%;
    }
  }
`;

const SubText = styled.div`
  font-size: 24px;
  font-weight: 400;
  margin: 40px 0;
  color: #485055;
  max-width: 440px;
  text-align: center;
`;
